/**
 * Session analysis: turn a recorded pitch trace into tailored, objective feedback
 * (per-swara accuracy, steadiness/consistency, the moments that went out of scale,
 * and gamaka adherence). Pure and unit-tested; the report is rendered in the
 * client, and can also be handed to an LLM for a narrative coaching note.
 */
import { gradePitch, pitchVelocityCents, smoothPitchHz, TRANSITION_VELOCITY_CPS, } from './evaluate.js';
const MIN_SWARA_SAMPLES = 5;
const MIN_EPISODE_SEC = 0.35;
/** Minimum duration (ms) a run of the same swara must span to count as a note-event for gamaka scoring. */
const MIN_NOTE_MS = 150;
/** Below this peak-to-peak cents extent, an oscillating swara reads as sung flat (no gamaka). */
export const HELD_FLAT_CENTS = 20;
/** Above this peak-to-peak cents extent, a steady swara reads as wavered (unwanted oscillation). */
export const WAVER_CENTS = 45;
/**
 * Whether a raga's gamaka profile for a swara expects audible pitch movement
 * (kampita/jaru/nokku/orikai, or a steady swara explicitly marked non-holdable)
 * rather than a plain held/steady note. Shared by session analysis (this file)
 * and composition-mode per-note scoring (dtw/score.ts) so the two never drift.
 */
export function isOscillatingGamaka(profile) {
    return profile.style !== 'steady' || profile.hold_allowed === false;
}
function mean(xs) {
    return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0;
}
function std(xs, m) {
    if (xs.length < 2)
        return 0;
    return Math.sqrt(xs.reduce((a, b) => a + (b - m) ** 2, 0) / xs.length);
}
function mmss(sec) {
    const s = Math.max(0, Math.round(sec));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
const fold = (c) => ((c % 1200) + 1200) % 1200;
const DEFAULT_GAMAKA_PROFILE = { style: 'steady', hold_allowed: true };
export function analyzeSession(samples, saHz, targetCents, gamakaProfile, level) {
    const empty = {
        durationSec: 0,
        voicedSamples: 0,
        inRagaPct: 0,
        centeredPct: 0,
        consistency: 0,
        perSwara: [],
        strongest: [],
        weakest: [],
        episodes: [],
        gamaka: { adherencePct: 100, flagged: [] },
        summary: ['No audio was captured — press Start practice, then sing.'],
    };
    const allowed = Object.keys(targetCents);
    if (samples.length === 0 || allowed.length === 0)
        return empty;
    const sorted = [...samples].sort((a, b) => a.t - b.t);
    const durationSec = (sorted[sorted.length - 1].t - sorted[0].t) / 1000;
    // Grade the SMOOTHED pitch so a gamaka oscillation reads as its centre swara
    // rather than a smear through neighbours; velocity is taken on the smoothed
    // series too, so only genuine note-to-note slides count as transitions. Gamaka
    // extent (below) is measured on the RAW pitch, since that's exactly the
    // oscillation the smoothing is designed to collapse away.
    const frames = [];
    let prev = null;
    for (let i = 0; i < sorted.length; i++) {
        const s = sorted[i];
        const smoothHz = smoothPitchHz(sorted, i, saHz);
        const g = gradePitch(smoothHz, saHz, targetCents, level);
        const transition = prev !== null &&
            Math.abs(pitchVelocityCents(prev.hz, prev.t / 1000, smoothHz, s.t / 1000)) >
                TRANSITION_VELOCITY_CPS;
        frames.push({
            t: s.t,
            rawHz: s.freqHz,
            smoothHz,
            symbol: g.nearestSwara,
            cents: g.centsFromNearest,
            band: g.band,
            transition,
        });
        prev = { hz: smoothHz, t: s.t };
    }
    if (frames.length === 0)
        return empty;
    const graded = frames.filter((f) => !f.transition); // ignore slides in the scores
    const scoreBase = graded.length || 1;
    const inRagaPct = (100 * graded.filter((f) => f.band !== 'wrong').length) / scoreBase;
    const centeredPct = (100 * graded.filter((f) => f.band === 'excellent').length) / scoreBase;
    // Per-swara aggregation over in-raga (non-wrong) frames.
    const bySymbol = new Map();
    for (const f of graded) {
        if (f.band === 'wrong')
            continue;
        const symbol = f.symbol;
        const grp = bySymbol.get(symbol) ?? { cents: [], excellent: 0 };
        grp.cents.push(f.cents);
        if (f.band === 'excellent')
            grp.excellent++;
        bySymbol.set(symbol, grp);
    }
    const perSwara = [...bySymbol.entries()]
        .map(([symbol, grp]) => {
        const m = mean(grp.cents);
        return {
            symbol,
            samples: grp.cents.length,
            timePct: (100 * grp.cents.length) / scoreBase,
            meanCents: m,
            stdCents: std(grp.cents, m),
            inTunePct: (100 * grp.excellent) / grp.cents.length,
        };
    })
        .sort((a, b) => b.inTunePct - a.inTunePct);
    const totalSamples = perSwara.reduce((a, s) => a + s.samples, 0);
    const weightedStd = perSwara.reduce((a, s) => a + s.stdCents * s.samples, 0) / (totalSamples || 1);
    const consistency = Math.max(0, Math.min(100, Math.round(100 - weightedStd)));
    const meaningful = perSwara.filter((s) => s.samples >= MIN_SWARA_SAMPLES);
    const strongest = meaningful.slice(0, 3);
    const weakest = [...meaningful].reverse().slice(0, 3);
    // Out-of-raga episodes: contiguous non-transition `wrong` runs.
    const episodes = [];
    const t0 = frames[0].t;
    let run = [];
    const flush = () => {
        if (run.length === 0)
            return;
        const startSec = (run[0].t - t0) / 1000;
        const endSec = (run[run.length - 1].t - t0) / 1000;
        if (endSec - startSec >= MIN_EPISODE_SEC) {
            const counts = new Map();
            for (const f of run) {
                const symbol = f.symbol;
                counts.set(symbol, (counts.get(symbol) ?? 0) + 1);
            }
            const swara = [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0];
            episodes.push({ startSec, endSec, swara, centsFromScale: mean(run.map((f) => f.cents)) });
        }
        run = [];
    };
    for (const f of frames) {
        if (f.band === 'wrong' && !f.transition)
            run.push(f);
        else
            flush();
    }
    flush();
    // Gamaka scoring: segment the graded, in-raga (non-transition, band !== 'wrong')
    // frames into note-events — maximal runs of the same nearest swara spanning at
    // least MIN_NOTE_MS — and check each event's raw-pitch oscillation extent
    // against the raga's gamaka profile for that swara (defaulting to
    // steady/hold-allowed when un-enriched, so un-enriched ragas never get flagged
    // held-flat). `wrong`-band frames are excluded entirely: a badly out-of-tune
    // note that happens to be nearest an oscillating swara is a wrong-note problem,
    // not a gamaka problem, and must never be verdicted held-flat/wavered.
    const gamakaFrames = graded.filter((f) => f.band !== 'wrong');
    const events = [];
    let curSymbol = null;
    let curFrames = [];
    const flushEvent = () => {
        if (curFrames.length === 0 || curSymbol === null) {
            curFrames = [];
            return;
        }
        const startMs = curFrames[0].t;
        const endMs = curFrames[curFrames.length - 1].t;
        if (endMs - startMs >= MIN_NOTE_MS) {
            // Unwrap each frame's raw cents relative to the event's own first frame
            // (same trick gradePitch uses against a single target) so a run that
            // straddles the Sa/octave seam (0/1200¢) doesn't read as a ~1200¢ swing.
            // Peak-to-peak is shift-invariant, so the choice of reference frame
            // doesn't affect the resulting extent.
            const ref = fold(1200 * Math.log2(curFrames[0].rawHz / saHz));
            const unwrappedCents = curFrames.map((f) => {
                const pc = fold(1200 * Math.log2(f.rawHz / saHz));
                let d = pc - ref;
                if (d > 600)
                    d -= 1200;
                else if (d < -600)
                    d += 1200;
                return d;
            });
            events.push({ symbol: curSymbol, startMs, unwrappedCents });
        }
        curFrames = [];
    };
    for (const f of gamakaFrames) {
        if (f.symbol !== curSymbol) {
            flushEvent();
            curSymbol = f.symbol;
        }
        curFrames.push(f);
    }
    flushEvent();
    const flagged = [];
    let okEvents = 0;
    for (const ev of events) {
        const extent = Math.max(...ev.unwrappedCents) - Math.min(...ev.unwrappedCents);
        const prof = gamakaProfile[ev.symbol] ?? DEFAULT_GAMAKA_PROFILE;
        const oscillating = isOscillatingGamaka(prof);
        if (oscillating && extent < HELD_FLAT_CENTS) {
            flagged.push({ swara: ev.symbol, verdict: 'held-flat', startSec: (ev.startMs - t0) / 1000 });
        }
        else if (!oscillating && extent > WAVER_CENTS) {
            flagged.push({ swara: ev.symbol, verdict: 'wavered', startSec: (ev.startMs - t0) / 1000 });
        }
        else {
            okEvents++;
        }
    }
    const gamakaAdherencePct = events.length ? (100 * okEvents) / events.length : 100;
    const summary = [
        `In the raga ${Math.round(inRagaPct)}% over ${mmss(durationSec)} (well-centred ${Math.round(centeredPct)}%).`,
    ];
    if (strongest.length)
        summary.push(`Steadiest: ${strongest.map((s) => s.symbol).join(', ')}.`);
    if (episodes.length) {
        summary.push(`Slipped out of the raga around ${episodes
            .slice(0, 4)
            .map((e) => `${mmss(e.startSec)}–${mmss(e.endSec)}`)
            .join(', ')}.`);
    }
    return {
        durationSec,
        voicedSamples: frames.length,
        inRagaPct,
        centeredPct,
        consistency,
        perSwara,
        strongest,
        weakest,
        episodes,
        gamaka: { adherencePct: gamakaAdherencePct, flagged },
        summary,
    };
}
//# sourceMappingURL=analysis.js.map